// smoothDamp — critically-damped smoothing of a scalar toward a target.
//
// The maths behind the Smooth Damp event-graph node (ENG-1127). A faithful
// transcription of Unity's `Mathf.SmoothDamp`, which is the same model as
// Unreal's `FMath::CriticallyDampedSmoothing`
// (Engine/Source/Runtime/Core/Public/Math/UnrealMathUtility.h) — both integrate
// a critically-damped spring at natural frequency `w = 2/smoothTime` using a
// rational approximation of exp(-w·dt) rather than calling exp(). Only the
// coefficients differ (Unity 1/(1+x+0.48x²+0.235x³), Unreal
// 1/(1+1.00746x+0.45054x²+0.25725x³)); we ship Unity's, per the ticket.
//
// "Critically damped" is the whole point: the value eases into the target and
// STOPS there — no overshoot, no ringing — and because the step is expressed in
// dt, the trajectory is the same whether it is integrated at 60 or 144 Hz. A
// hand-rolled `current += (target-current)*k` is neither: it overshoots a moving
// target and its speed scales with the frame rate.
//
// `smoothTime` is roughly "time to reach the target"; more precisely it is the
// lag when tracking a target moving at constant speed.
//
// Three details are not decoration and each is a real defect if dropped:
//   • smoothTime is FLOORED (0 divides by zero through `omega`),
//   • dt <= 0 returns the input untouched — the overshoot branch below divides
//     by dt, and at current === target it is always taken, so a zero-dt frame
//     would return NaN on exactly the values that are already correct,
//   • the overshoot clamp stops the output crossing the target, which is what
//     keeps a MOVING target (re-read every frame by the node) from ringing.
//
// Pure, no imports, unit-tested in tests/unit/smoothDamp.test.mjs. The node's
// per-frame loop in EventRuntime is a thin adapter around this.

/** Unity's floor; `smoothTime` 0 would divide by zero through `omega`. */
export const SMOOTH_TIME_FLOOR = 1e-4

/**
 * One critically-damped step toward `target`.
 *
 * @param {number} current    the value now
 * @param {number} target     where it is heading (may move between calls)
 * @param {number} velocity   the carried velocity — feed back the returned one
 * @param {number} smoothTime seconds, roughly "time to reach the target"
 * @param {number} dt         seconds elapsed since the last step
 * @param {{maxSpeed?:number, epsilon?:number}} [opts]
 *        maxSpeed — units/second cap, 0 = unlimited
 *        epsilon  — settle threshold for the returned `done`
 * @returns {{value:number, velocity:number, done:boolean}}
 */
export function smoothDamp(current, target, velocity, smoothTime, dt, opts = {}) {
  const maxSpeed = opts.maxSpeed ?? 0
  const epsilon  = opts.epsilon ?? 0.001

  // A zero (or backwards) step advances nothing — and must not reach the
  // divide-by-dt in the overshoot branch.
  if (!(dt > 0)) return { value: current, velocity, done: settled(current, target, velocity, dt, epsilon) }

  const st    = Math.max(SMOOTH_TIME_FLOOR, smoothTime)
  const omega = 2 / st
  const x     = omega * dt
  // exp(-x), to within ~0.1% over the useful range, with no exp() call. Strictly
  // positive and decreasing for x > 0, so a huge dt degrades to "snap to the
  // target", never to a blow-up.
  const e     = 1 / (1 + x + 0.48 * x * x + 0.235 * x * x * x)

  const originalTarget = target
  let change = current - target

  // Speed cap, expressed as a cap on how far the spring is allowed to be
  // stretched — that is what bounds the velocity it can develop.
  if (maxSpeed > 0) {
    const maxChange = maxSpeed * st
    change = Math.min(maxChange, Math.max(-maxChange, change))
  }
  target = current - change

  const temp = (velocity + omega * change) * dt
  let nextVelocity = (velocity - omega * temp) * e
  let value        = target + (change + temp) * e

  // Overshoot clamp: if the step would carry the value past the target (or if it
  // is already sitting on it), land exactly on the target with zero velocity.
  if ((originalTarget - current > 0) === (value > originalTarget)) {
    value = originalTarget
    nextVelocity = 0
  }

  return { value, velocity: nextVelocity, done: settled(value, originalTarget, nextVelocity, dt, epsilon) }
}

/**
 * Has it arrived? Both the gap AND the travel one more frame would make must be
 * inside epsilon — a value that happens to pass through the target at speed is
 * not settled, it is mid-flight.
 */
function settled(value, target, velocity, dt, epsilon) {
  const step = dt > 0 ? Math.abs(velocity) * dt : Math.abs(velocity)
  return Math.abs(value - target) <= epsilon && step <= epsilon
}
